import React, { useMemo, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Chip,
  Snackbar,
  Alert,
  Fab,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Card,
  CardContent,
  LinearProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Public as PublicIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
  Route as RouteIcon,
  LocationOn as LocationOnIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { DataGrid, esES } from "@mui/x-data-grid";

import RouteForm from "./RouteForm";
import RouteMap from "./RouteMap";
import { useRoutes } from "../../../hooks/useRoutes";
import { useAuthContext } from "../../../context/AuthProvider";

const STATUS_OPTIONS = {
  ALL: "ALL",
  ACTIVE: "A",
  INACTIVE: "NOT_A",
};

const getRouteStopsCount = (route) => {
  if (!Array.isArray(route?.paradas)) return 0;
  return route.paradas.length;
};

const getRouteSummary = (route) => {
  const stops = Array.isArray(route?.paradas) ? route.paradas : [];
  const ordered = [...stops].sort(
    (a, b) => Number(a.parord || 0) - Number(b.parord || 0)
  );

  const origin =
    ordered.find((p) => p.partip === "ORIGEN") || ordered[0] || null;

  const destination =
    ordered.find((p) => p.partip === "DESTINO") ||
    ordered[ordered.length - 1] ||
    null;

  return {
    totalStops: ordered.length,
    originName: origin?.locnom || route?.manori || "N/A",
    destinationName: destination?.locnom || route?.mandes || "N/A",
  };
};

const RoutesDashboard = () => {
  const [openForm, setOpenForm] = useState(false);
  const [openMap, setOpenMap] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);

  const [routeToDelete, setRouteToDelete] = useState(null);

  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(STATUS_OPTIONS.ALL);

  const { user } = useAuthContext();

  const {
    routes = [],
    isLoading,
    error,
    createMutation,
    updateMutation,
    deleteMutation,
    refetch,
  } = useRoutes();

  const isMutating =
    createMutation?.isPending ||
    updateMutation?.isPending ||
    deleteMutation?.isPending;

  const showAlert = useCallback((message, severity = "success") => {
    setAlert({ open: true, message, severity });
  }, []);

  const handleOpenCreate = useCallback(() => {
    setSelectedRoute(null);
    setOpenForm(true);
  }, []);

  const handleOpenEdit = useCallback((route) => {
    setSelectedRoute(route);
    setOpenForm(true);
  }, []);

  const handleOpenMap = useCallback((route) => {
    setSelectedRoute(route);
    setOpenMap(true);
  }, []);

  const handleCloseForm = useCallback(() => {
    setOpenForm(false);
    setSelectedRoute(null);
  }, []);

  const handleCloseMap = useCallback(() => {
    setOpenMap(false);
    setSelectedRoute(null);
  }, []);

  const handleAskDelete = useCallback((route) => {
    setRouteToDelete(route);
  }, []);

  const handleCancelDelete = useCallback(() => {
    setRouteToDelete(null);
  }, []);

  const handleSubmitRoute = useCallback(
    (data) => {
      if (!user?.EmpID) {
        showAlert("Debe iniciar sesión para realizar esta acción.", "error");
        return;
      }

      const isEditing = Boolean(selectedRoute?.rutid);

      const payload = isEditing
        ? {
            id: selectedRoute.rutid,
            data: {
              ...data,
              updbyid: user.EmpID,
            },
          }
        : {
            ...data,
            crebyid: user.EmpID,
          };

      const mutation = isEditing ? updateMutation : createMutation;

      mutation.mutate(payload, {
        onSuccess: () => {
          showAlert(
            isEditing ? "Ruta actualizada correctamente." : "Ruta creada correctamente.",
            "success"
          );
          handleCloseForm();
        },
        onError: (err) => {
          showAlert(
            err?.response?.data?.error ||
              err?.response?.data?.message ||
              err?.message ||
              "Error al guardar la ruta.",
            "error"
          );
        },
      });
    },
    [
      user,
      selectedRoute,
      createMutation,
      updateMutation,
      showAlert,
      handleCloseForm,
    ]
  );

  const handleConfirmDelete = useCallback(() => {
    if (!routeToDelete?.rutid) return;

    deleteMutation.mutate(routeToDelete.rutid, {
      onSuccess: () => {
        showAlert("Ruta desactivada correctamente.", "success");
        setRouteToDelete(null);
      },
      onError: (err) => {
        showAlert(
          err?.response?.data?.error ||
            err?.response?.data?.message ||
            err?.message ||
            "Error al desactivar la ruta.",
          "error"
        );
      },
    });
  }, [routeToDelete, deleteMutation, showAlert]);

  const normalizedSearch = search.trim().toLowerCase();

  const filteredRows = useMemo(() => {
    let data = Array.isArray(routes) ? routes : [];

    if (statusFilter === STATUS_OPTIONS.ACTIVE) {
      data = data.filter((r) => r.estreg === "A");
    }

    if (statusFilter === STATUS_OPTIONS.INACTIVE) {
      data = data.filter((r) => r.estreg !== "A");
    }

    if (normalizedSearch) {
      data = data.filter((r) => {
        const summary = getRouteSummary(r);

        return (
          String(r.rutnom || "").toLowerCase().includes(normalizedSearch) ||
          String(r.rutdes || "").toLowerCase().includes(normalizedSearch) ||
          String(r.pronom || "").toLowerCase().includes(normalizedSearch) ||
          String(summary.originName || "").toLowerCase().includes(normalizedSearch) ||
          String(summary.destinationName || "")
            .toLowerCase()
            .includes(normalizedSearch)
        );
      });
    }

    return data;
  }, [routes, statusFilter, normalizedSearch]);

  const metrics = useMemo(() => {
    const total = routes.length;
    const active = routes.filter((r) => r.estreg === "A").length;
    const inactive = total - active;

    const withStops = routes.filter((r) => getRouteStopsCount(r) >= 2).length;

    return {
      total,
      active,
      inactive,
      withStops,
    };
  }, [routes]);

  const columns = useMemo(
    () => [
      {
        field: "rutnom",
        headerName: "Ruta",
        flex: 1.2,
        minWidth: 240,
        renderCell: (params) => {
          const summary = getRouteSummary(params.row);

          return (
            <Box sx={{ py: 0.5 }}>
              <Typography variant="body2" fontWeight={700}>
                {params.value || "Sin nombre"}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                {summary.originName} → {summary.destinationName}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "rutdes",
        headerName: "Descripción",
        flex: 1,
        minWidth: 220,
        valueGetter: (params) => params.row?.rutdes || "N/A",
      },
      {
        field: "pronom",
        headerName: "Proyecto",
        flex: 0.8,
        minWidth: 180,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.row?.pronom || "Sin proyecto"}
            variant="outlined"
          />
        ),
      },
      {
        field: "paradas",
        headerName: "Paradas",
        width: 120,
        align: "center",
        headerAlign: "center",
        sortable: false,
        renderCell: (params) => {
          const total = getRouteStopsCount(params.row);
          const isComplete = total >= 2;

          return (
            <Chip
              size="small"
              icon={<LocationOnIcon />}
              label={total}
              color={isComplete ? "primary" : "warning"}
              variant={isComplete ? "outlined" : "filled"}
              sx={{ fontWeight: 700 }}
            />
          );
        },
      },
      {
        field: "estreg",
        headerName: "Estado",
        width: 135,
        renderCell: (params) => {
          const isActive = params.value === "A";

          return (
            <Chip
              size="small"
              icon={isActive ? <CheckCircleIcon /> : <CancelIcon />}
              label={isActive ? "ACTIVO" : "INACTIVO"}
              color={isActive ? "success" : "warning"}
              variant="outlined"
              sx={{ fontWeight: 700, fontSize: "0.7rem" }}
            />
          );
        },
      },
      {
        field: "actions",
        headerName: "Acciones",
        width: 160,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Ver mapa">
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleOpenMap(params.row)}
                  disabled={getRouteStopsCount(params.row) < 2}
                >
                  <PublicIcon color="info" fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Editar ruta">
              <IconButton size="small" onClick={() => handleOpenEdit(params.row)}>
                <EditIcon color="primary" fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Desactivar ruta">
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleAskDelete(params.row)}
                  disabled={params.row?.estreg !== "A"}
                >
                  <DeleteIcon
                    color={params.row?.estreg === "A" ? "error" : "disabled"}
                    fontSize="small"
                  />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [handleOpenEdit, handleOpenMap, handleAskDelete]
  );

  const handleClearFilters = () => {
    setSearch("");
    setStatusFilter(STATUS_OPTIONS.ALL);
  };

  return (
    <Paper
      sx={{
        p: 2.5,
        borderRadius: 3,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
      }}
    >
      <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          gap={2}
          flexDirection={{ xs: "column", md: "row" }}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <RouteIcon color="primary" />
              <Typography variant="h5" fontWeight={800}>
                Rutas Logísticas
              </Typography>
            </Stack>

            <Typography variant="body2" color="text.secondary">
              Gestiona rutas plantilla, paradas, horarios estimados y visualización en mapa.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Actualizar">
              <IconButton onClick={() => refetch?.()} disabled={isLoading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
            >
              Nueva ruta
            </Button>
          </Stack>
        </Box>

        {isMutating && <LinearProgress />}

        <Box
          display="grid"
          gridTemplateColumns={{
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(4, 1fr)",
          }}
          gap={1.5}
        >
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Total
              </Typography>
              <Typography variant="h5" fontWeight={800}>
                {metrics.total}
              </Typography>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Activas
              </Typography>
              <Typography variant="h5" fontWeight={800} color="success.main">
                {metrics.active}
              </Typography>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Inactivas
              </Typography>
              <Typography variant="h5" fontWeight={800} color="warning.main">
                {metrics.inactive}
              </Typography>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Con paradas configuradas
              </Typography>
              <Typography variant="h5" fontWeight={800} color="primary.main">
                {metrics.withStops}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
            spacing={1.5}
          >
            <TextField
              size="small"
              placeholder="Buscar por ruta, proyecto, origen o destino..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch("")}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: { xs: "100%", md: 420 } }}
            />

            <Stack direction="row" spacing={1} alignItems="center">
              <ToggleButtonGroup
                size="small"
                exclusive
                value={statusFilter}
                onChange={(_, val) => val && setStatusFilter(val)}
              >
                <ToggleButton value={STATUS_OPTIONS.ALL}>Todos</ToggleButton>
                <ToggleButton value={STATUS_OPTIONS.ACTIVE}>Activos</ToggleButton>
                <ToggleButton value={STATUS_OPTIONS.INACTIVE}>
                  Inactivos
                </ToggleButton>
              </ToggleButtonGroup>

              {(search || statusFilter !== STATUS_OPTIONS.ALL) && (
                <Button size="small" onClick={handleClearFilters}>
                  Limpiar
                </Button>
              )}
            </Stack>
          </Stack>
        </Paper>

        {error && (
          <Alert severity="error">
            {error?.response?.data?.error ||
              error?.response?.data?.message ||
              error?.message ||
              "Error al cargar rutas."}
          </Alert>
        )}

        <Box sx={{ flex: 1, minHeight: 0 }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            loading={isLoading}
            getRowId={(row) => row.rutid}
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10, page: 0 } },
              sorting: {
                sortModel: [{ field: "rutnom", sort: "asc" }],
              },
            }}
            localeText={esES.components.MuiDataGrid.defaultProps.localeText}
            density="comfortable"
            sx={{
              border: "none",
              bgcolor: "background.paper",
              borderRadius: 2,
              "& .MuiDataGrid-columnHeaders": {
                bgcolor: "#f8fafc",
                fontWeight: 800,
                borderBottom: "1px solid #e5e7eb",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #f1f5f9",
              },
              "& .MuiDataGrid-row:hover": {
                bgcolor: "#f8fbff",
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "1px solid #e5e7eb",
              },
            }}
          />
        </Box>
      </Stack>

      <Fab
        color="primary"
        aria-label="add"
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          display: { xs: "flex", md: "none" },
        }}
        onClick={handleOpenCreate}
      >
        <AddIcon />
      </Fab>

      <Dialog
        open={openMap}
        onClose={handleCloseMap}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={800}>
              Mapa de Ruta
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedRoute?.rutnom || "Ruta seleccionada"}
            </Typography>
          </Box>

          <IconButton onClick={handleCloseMap} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ p: 0, height: { xs: 480, md: 620 } }}>
          {selectedRoute && <RouteMap route={selectedRoute?.paradas || []} />}
        </DialogContent>
      </Dialog>

      {openForm && (
        <RouteForm
          open={openForm}
          handleClose={handleCloseForm}
          onSubmit={handleSubmitRoute}
          defaultValues={selectedRoute || {}}
        />
      )}

      <Dialog
        open={Boolean(routeToDelete)}
        onClose={handleCancelDelete}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={800}>Desactivar ruta</DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            ¿Está seguro de desactivar la ruta{" "}
            <strong>{routeToDelete?.rutnom}</strong>?
          </Typography>

          <Alert severity="warning" sx={{ mt: 2 }}>
            Esta acción no eliminará físicamente la ruta, pero dejará de estar
            disponible como ruta activa.
          </Alert>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancelar</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={deleteMutation?.isPending}
          >
            Desactivar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={alert.open}
        autoHideDuration={3500}
        onClose={() => setAlert((a) => ({ ...a, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={alert.severity} variant="filled" sx={{ borderRadius: 2 }}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default RoutesDashboard;